import dictionary from '@/components/GamePlay/Games/WordWheel/data/wordList.json';

// Stub for NLP capabilities if compromise.js isn't available
const nlpStub = {
  // Simple stub that always returns false for NLP checks
  has: () => false,
  // Add a dummy method that can be called safely
  parse: (text: string) => ({ has: () => false }),
};

// Only conditionally import compromise to avoid errors if it's not available
// In a real project, you'd do a proper dynamic import, but for this demo, we'll use a stub
const nlp = nlpStub;

interface WordDictionary {
  [key: string]: string[];
}

// Define common properties of letter banks for reuse
const CONSONANT_BANK: string[] = [
  'M',
  'N',
  'S',
  'G',
  'D',
  'H',
  'F',
  'R',
  'T',
  'B',
  'W',
];
const VOWEL_BANK: string[] = ['A', 'O', 'I', 'U', 'E', 'Y'];

// Common stop words that should be detected and avoided
const COMMON_STOP_WORDS: string[] = [
  // Two-letter stop words
  'AM',
  'AN',
  'AS',
  'AT',
  'BE',
  'BY',
  'DO',
  'GO',
  'HE',
  'HI',
  'IF',
  'IN',
  'IS',
  'IT',
  'ME',
  'MY',
  'NO',
  'OF',
  'OK',
  'ON',
  'OR',
  'SO',
  'TO',
  'UP',
  'US',
  'WE',

  // Common three-letter+ stop words
  'THE',
  'AND',
  'FOR',
  'BUT',
  'NOT',
  'YOU',
  'ALL',
  'ANY',
  'CAN',
  'HAD',
  'HAS',
  'HER',
  'HIS',
  'OUR',
  'ITS',
  'WHO',
  'ARE',
  'THEY',
  'THAT',
  'THIS',
  'WHAT',
  'WHEN',
  'WHERE',
  'WHY',
  'HOW',
  'WITH',
  'FROM',
  'OUT',
  'OVER',
];

export class SafeStringGenerator {
  private readonly wordDictionaries: WordDictionary;
  private readonly maxWordLength: number;
  private readonly stopWordSet: Set<string>;
  private readonly nlp: typeof nlp;

  constructor(maxCheckLength: number = 7) {
    // Initialize dictionaries from the imported dictionary
    const typedDictionary = dictionary as WordDictionary;
    this.wordDictionaries = typedDictionary;

    // Determine the maximum word length to check, based on available dictionaries
    const availableLengths = Object.keys(typedDictionary).map((key) =>
      parseInt(key, 10)
    );
    const maxAvailableLength =
      availableLengths.length > 0 ? Math.max(...availableLengths) : 5; // Default if no dictionaries

    this.maxWordLength = Math.min(maxCheckLength, maxAvailableLength);

    // Create a comprehensive Set by combining COMMON_STOP_WORDS with all dictionary words
    // This ensures we catch all real words, not just the hardcoded stop words
    const allWords = new Set<string>([
      ...COMMON_STOP_WORDS,
      ...COMMON_STOP_WORDS.map((w) => w.toLowerCase()),
    ]);

    // Add all words from the dictionary to the set
    Object.values(typedDictionary).forEach((wordList) => {
      wordList.forEach((word) => {
        allWords.add(word.toUpperCase());
        allWords.add(word.toLowerCase());
      });
    });

    this.stopWordSet = allWords;

    // Keep a reference to the NLP stub (can be replaced with real NLP library later)
    this.nlp = nlp;

    // Log initialization status
    console.log(
      `SafeStringGenerator initialized with max word length: ${this.maxWordLength}`
    );
    console.log(`Using simplified word detection (no NLP)`);

    // Log the available dictionaries for debugging
    const dictKeys = Object.keys(this.wordDictionaries);
    console.log(`Available word dictionaries: ${dictKeys.join(', ')}`);
    console.log(`Total words in stopWordSet: ${this.stopWordSet.size}`);

    // Debug: Check if specific 2-letter words are in the set
    const testWords = ['WE', 'HI', 'IF', 'AM', 'AN'];
    console.log('Testing if sample 2-letter words are in stopWordSet:');
    testWords.forEach((word) => {
      console.log(`  ${word}: ${this.stopWordSet.has(word)}`);
    });
  }

  /**
   * Generates a random letter, optionally excluding specific letters
   * @param exclude - Letters to exclude from selection
   * @param preferVowel - Prefer a vowel or consonant (undefined for random)
   * @returns A random letter
   */
  private generateRandomLetter(
    exclude: string[] = [],
    preferVowel?: boolean
  ): string {
    // Determine which bank to use
    let bank: string[];

    if (preferVowel === undefined) {
      // Random selection between vowels and consonants
      bank = Math.random() > 0.5 ? VOWEL_BANK : CONSONANT_BANK;
    } else {
      // Use the specified preference
      bank = preferVowel ? VOWEL_BANK : CONSONANT_BANK;
    }

    // Filter out excluded letters
    const validLetters = bank.filter((letter) => !exclude.includes(letter));

    // If all letters from the preferred bank are excluded, use the other bank
    if (validLetters.length === 0) {
      const alternateBank = preferVowel ? CONSONANT_BANK : VOWEL_BANK;
      return alternateBank[Math.floor(Math.random() * alternateBank.length)];
    }

    // Return a random letter from the valid options
    return validLetters[Math.floor(Math.random() * validLetters.length)];
  }

  /**
   * Replaces part of a string with random letters to break a detected word
   * @param string - The original string
   * @param startIndex - Starting index of the word to replace
   * @param wordLength - Length of the word to replace
   * @param preserveIndex - Optional index to preserve (not replace)
   * @returns Modified string with the word broken
   */
  private replaceWordWithRandomLetters(
    string: string,
    startIndex: number,
    wordLength: number,
    preserveIndex: number | null = null
  ): string {
    const newString = string.split('');
    const word = string.substring(startIndex, startIndex + wordLength);

    // Get letter type statistics from the original word
    const vowelCount = word
      .split('')
      .filter((c) => VOWEL_BANK.includes(c)).length;
    const consonantCount = wordLength - vowelCount;
    const needsMoreVowels = vowelCount < wordLength / 3; // Ensure some vowels

    // Replace a strategic position to break the word
    const replacePos =
      preserveIndex !== null
        ? preserveIndex >= startIndex && preserveIndex < startIndex + wordLength
          ? null // Don't replace preserveIndex
          : startIndex + Math.floor(wordLength / 2) // Replace middle
        : startIndex + Math.floor(wordLength / 2); // Replace middle

    if (replacePos !== null) {
      const currentLetter = newString[replacePos];
      const isVowel = VOWEL_BANK.includes(currentLetter);

      // Decide whether to replace with vowel or consonant
      // If word has too few vowels, add one; otherwise prefer opposite type
      const preferVowel = needsMoreVowels ? true : !isVowel;

      // Generate a new letter that's different from the current one
      let newLetter;
      do {
        newLetter = this.generateRandomLetter([currentLetter], preferVowel);
      } while (newLetter === currentLetter);

      newString[replacePos] = newLetter;
    }

    return newString.join('');
  }

  /**
   * Check if a given sequence contains real words and replace them if found
   * @param letterArray - Array of letters to check
   * @param minWordLength - Minimum word length to check for
   * @param maxWordLength - Maximum word length to check for
   * @param targetWord - Optional target word to avoid checking (the intended word)
   * @returns Sanitized array with no accidental words
   */
  public sanitizeLetterArray(
    letterArray: string[],
    minWordLength: number = 2,
    maxWordLength: number = 5,
    targetWord?: string
  ): string[] {
    if (!letterArray || letterArray.length < minWordLength) return letterArray;

    // Convert to string for easier processing
    let letterString = letterArray.join('');
    const originalString = letterString;
    let hasChanges = false;

    // Set upper bound based on available dictionaries and array length
    const upperBound = Math.min(
      maxWordLength,
      this.maxWordLength,
      letterArray.length
    );

    // Create a Set to track words we've already processed to avoid infinite loops
    const processedWords = new Set<string>();

    // Maximum number of changes to prevent infinite loops
    const MAX_CHANGES = 20;
    let changesCount = 0;

    // Check for words of different lengths
    for (let len = upperBound; len >= minWordLength; len--) {
      // Skip checking if this is our target word length
      if (targetWord && targetWord.length === len) continue;

      // Get the dictionary for this word length
      const dictionary = this.wordDictionaries[len.toString()];
      if (!dictionary) continue;

      // Sliding window check through the string
      for (let i = 0; i <= letterString.length - len; i++) {
        // Prevent excessive changes
        if (changesCount >= MAX_CHANGES) {
          console.warn(
            `⚠️ WARNING: Reached maximum number of changes (${MAX_CHANGES}), stopping checks. Sequence may still contain words!`
          );
          console.warn(`Current sequence: ${letterString}`);
          break;
        }

        const word = letterString.substring(i, i + len).toUpperCase();

        // Skip this word if we've already processed it
        if (processedWords.has(word)) continue;

        // Add to processed set to avoid checking again
        processedWords.add(word);

        // Since stopWordSet now contains ALL dictionary words (from constructor),
        // we can simply check against it for all word lengths
        const shouldReplace = this.stopWordSet.has(word) && word !== targetWord;

        if (shouldReplace) {
          console.log(
            `Found dictionary word: ${word} (length ${len}) - replacing`
          );

          // Replace the word
          letterString = this.replaceWordWithRandomLetters(
            letterString,
            i,
            len
          );

          hasChanges = true;
          changesCount++;

          // Restart checking at this position since we changed letters
          i = Math.max(0, i - 1);
        }
      }
    }

    // If changes were made, convert back to array
    if (hasChanges) {
      return letterString.split('');
    }

    // No changes needed
    return letterArray;
  }

  /**
   * Generates an array of random letters that doesn't contain any accidental words
   * @param length - Length of the array to generate
   * @param minWordLength - Minimum word length to check for
   * @param maxWordLength - Maximum word length to check for
   * @param targetWord - Optional target word to avoid checking
   * @returns Array of safe random letters
   */
  public generateSafeLetterArray(
    length: number = 12,
    minWordLength: number = 2,
    maxWordLength: number = 5,
    targetWord?: string
  ): string[] {
    // First generate a basic letter array with balanced vowels/consonants
    const letterArray: string[] = [];

    // Aim for approximately 30-40% vowels for readability
    const targetVowelCount = Math.floor(length * 0.35);
    let vowelCount = 0;

    for (let i = 0; i < length; i++) {
      // Determine if we need a vowel or consonant
      const vowelsRemaining = targetVowelCount - vowelCount;
      const positionsRemaining = length - i;
      const needVowel =
        vowelsRemaining > 0 && vowelsRemaining / positionsRemaining >= 0.3;

      // Generate the letter
      const letter = this.generateRandomLetter([], needVowel);
      letterArray.push(letter);

      // Update vowel count
      if (VOWEL_BANK.includes(letter)) {
        vowelCount++;
      }
    }

    // Special handling for short word dictionaries (2-3 letters)
    // Only check against a limited subset to avoid false positives
    // For 2-3 letter combinations, only replace if they're in the common stop words

    // Create temporary dictionaries for checking
    const tempDictionaries: WordDictionary = {};

    // For two-letter words, include both dictionary and stop words
    if (minWordLength <= 2 && maxWordLength >= 2) {
      const twoLetterDict = this.wordDictionaries['2'] || [];
      const twoLetterStopWords = [...this.stopWordSet].filter(
        (word) => word.length === 2
      );

      // Combine dictionary and stop words, removing duplicates
      tempDictionaries['2'] = [
        ...new Set([...twoLetterDict, ...twoLetterStopWords]),
      ];
      console.log(
        `Two-letter word dictionary size: ${tempDictionaries['2'].length}`
      );
    }

    // For three-letter words, prioritize stop words but include some common words
    if (minWordLength <= 3 && maxWordLength >= 3) {
      const threeLetterDict = this.wordDictionaries['3'] || [];
      const threeLetterStopWords = [...this.stopWordSet].filter(
        (word) => word.length === 3
      );

      // Take stop words and a subset of dictionary words
      const commonThreeLetterWords = threeLetterDict.slice(0, 50); // Take the first 50 (most common)
      tempDictionaries['3'] = [
        ...new Set([...threeLetterStopWords, ...commonThreeLetterWords]),
      ];
    }

    // For longer words, use the full dictionary
    for (let i = Math.max(4, minWordLength); i <= maxWordLength; i++) {
      if (this.wordDictionaries[i.toString()]) {
        tempDictionaries[i.toString()] = this.wordDictionaries[i.toString()];
      }
    }

    // Store the original dictionaries
    const originalDictionaries = this.wordDictionaries;

    try {
      // Temporarily replace dictionaries with our filtered version
      this.wordDictionaries = tempDictionaries;

      // Now sanitize to remove accidental words with the modified dictionaries
      const result = this.sanitizeLetterArray(
        letterArray,
        minWordLength,
        maxWordLength,
        targetWord
      );

      return result;
    } finally {
      // Restore original dictionaries
      this.wordDictionaries = originalDictionaries;
    }
  }

  /**
   * Safely inserts a letter into a string and ensures no words are created
   * @param string - Original string
   * @param letter - Letter to insert
   * @param position - Position to insert at
   * @returns Modified string with letter inserted and no words
   */
  public insertLetter(
    string: string,
    letter: string,
    position: number
  ): string {
    const newString =
      string.substring(0, position) + letter + string.substring(position);

    // Convert to array, sanitize, and convert back
    const letterArray = newString.split('');
    const sanitizedArray = this.sanitizeLetterArray(letterArray);

    return sanitizedArray.join('');
  }

  /**
   * Generates a large pool of safe letters for efficient consumption
   * This creates a long sequence checked for accidental words throughout
   * @param poolSize - Total number of letters to generate (default 500)
   * @param minWordLength - Minimum word length to check for (default 2)
   * @param maxWordLength - Maximum word length to check for (default 7)
   * @returns Array of safe letters ready for consumption
   */
  public generateLetterPool(
    poolSize: number = 500,
    minWordLength: number = 2,
    maxWordLength: number = 7
  ): string[] {
    console.log(`Generating letter pool of size ${poolSize}...`);
    const startTime = Date.now();

    // Generate initial pool with balanced vowels/consonants
    const letterPool: string[] = [];
    const targetVowelRatio = 0.35; // 35% vowels
    const targetVowelCount = Math.floor(poolSize * targetVowelRatio);
    let vowelCount = 0;

    for (let i = 0; i < poolSize; i++) {
      const vowelsRemaining = targetVowelCount - vowelCount;
      const positionsRemaining = poolSize - i;
      const needVowel =
        vowelsRemaining > 0 &&
        vowelsRemaining / positionsRemaining >= targetVowelRatio * 0.8;

      const letter = this.generateRandomLetter([], needVowel);
      letterPool.push(letter);

      if (VOWEL_BANK.includes(letter)) {
        vowelCount++;
      }
    }

    console.log(
      `Initial pool generated with ${vowelCount} vowels (${((vowelCount / poolSize) * 100).toFixed(1)}%)`
    );

    // Now sanitize the entire pool in chunks to avoid overwhelming the checker
    // We'll check in overlapping windows to catch words that span chunk boundaries
    const CHUNK_SIZE = 50;
    const OVERLAP = maxWordLength; // Overlap to catch cross-boundary words

    for (let i = 0; i < letterPool.length; i += CHUNK_SIZE - OVERLAP) {
      const chunkStart = i;
      const chunkEnd = Math.min(i + CHUNK_SIZE, letterPool.length);
      const chunk = letterPool.slice(chunkStart, chunkEnd);

      // Sanitize this chunk
      const sanitizedChunk = this.sanitizeLetterArray(
        chunk,
        minWordLength,
        maxWordLength
      );

      // Replace the chunk in the pool (preserve overlap for next iteration)
      for (let j = 0; j < sanitizedChunk.length; j++) {
        const poolIndex = chunkStart + j;
        if (poolIndex < letterPool.length) {
          letterPool[poolIndex] = sanitizedChunk[j];
        }
      }

      // Log progress every 10 chunks
      if ((i / (CHUNK_SIZE - OVERLAP)) % 10 === 0) {
        const progress = ((chunkEnd / letterPool.length) * 100).toFixed(1);
        console.log(`Pool sanitization progress: ${progress}%`);
      }
    }

    const elapsed = Date.now() - startTime;
    console.log(
      `Letter pool generation complete in ${elapsed}ms (${(poolSize / elapsed).toFixed(1)} letters/ms)`
    );

    return letterPool;
  }

  /**
   * Legacy method - generates a safe string with no real words
   * @returns A string of random letters with no words
   */
  public generateSafeString(): string {
    return this.generateSafeLetterArray().join('');
  }
}
